/**
 * A CONFIRMAÇÃO E O AVISO do admin atual, no lugar de `mostrarConfirmacao` e `mostrarAlerta`
 * (`admin.html:7661` a `7698`; peça transversal 4 do inventário, linha 237).
 *
 * O CONTRATO É O DO LEGADO: `await confirmar('...')` devolve `true` só no OK, e `false` no
 * Cancelar, no X, no ESC e no clique fora. `alertar` é o aviso: título "Aviso" (ou o que a chamada
 * passar), sem Cancelar, e o botão "Entendi". A tela desenha o `dialogo` uma vez, em qualquer lugar.
 *
 * O ACHADO 5 (consulta sem X e sem botões, saindo só por ESC ou clique fora) vale aqui também, e
 * mora no `ModalLegado`, num ponto só.
 *
 * Dona desta peça: a frente A5 (Financeiro e Sistema), por designação do principal em 19/09/2026.
 */

// Estado e memória do React.
import { useCallback, useState } from 'react';
// O botão do Chassi.
import { Botao } from '@complexity/ui';
// O modal do legado, com o clique fora e o achado 5.
import { ModalLegado } from './modal-legado.js';

/** As opções do `mostrarConfirmacao` do legado, com os mesmos nomes. */
export interface OpcoesConfirmacao {
  readonly titulo?: string;
  readonly textoConfirmar?: string;
  readonly textoCancelar?: string;
  readonly somenteOk?: boolean;
}

// O pedido aberto: a mensagem, as opções e quem espera a resposta.
interface Pedido extends OpcoesConfirmacao {
  readonly mensagem: string;
  readonly responder: (sim: boolean) => void;
}

export function useConfirmacao() {
  const [pedido, setPedido] = useState<Pedido | null>(null);
  // Abre o modal e devolve a promessa que só resolve quando a pessoa decide.
  const confirmar = useCallback(
    (mensagem: string, opcoes: OpcoesConfirmacao = {}) =>
      new Promise<boolean>((resolver) => {
        setPedido({ mensagem, ...opcoes, responder: resolver });
      }),
    [],
  );
  // O aviso, com as opções que o `mostrarAlerta` do legado passa.
  const alertar = useCallback(
    (mensagem: string, titulo?: string) =>
      confirmar(mensagem, { titulo: titulo || 'Aviso', somenteOk: true, textoConfirmar: 'Entendi' }),
    [confirmar],
  );
  // Fecha e responde uma vez só; o segundo fechamento (o `close` nativo depois do clique) é ignorado.
  const encerrar = useCallback(
    (sim: boolean) => {
      setPedido((atual) => {
        atual?.responder(sim);
        return null;
      });
    },
    [],
  );
  const dialogo = (
    <ModalLegado
      aberto={pedido !== null}
      aoFechar={() => encerrar(false)}
      titulo={pedido?.titulo || 'Confirmar'}
      testid="modal-confirmacao"
      rodape={
        <>
          {/* Cancelar some quando é só um aviso, como o `display: none` do legado. */}
          {!pedido?.somenteOk && (
            <Botao variante="secundario" onClick={() => encerrar(false)}>
              {pedido?.textoCancelar || 'Cancelar'}
            </Botao>
          )}
          <Botao onClick={() => encerrar(true)}>{pedido?.textoConfirmar || 'OK'}</Botao>
        </>
      }
    >
      {/* `whitespace-pre-line` porque a mensagem do legado pode ter "\n\n" (admin.html:8906). */}
      <p className="whitespace-pre-line text-texto-suave" data-testid="confirmacao-mensagem">
        {pedido?.mensagem}
      </p>
    </ModalLegado>
  );
  return { confirmar, alertar, dialogo };
}
